// pages/PlanosPagamento/DetalhesPlanoPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon,
  Payment as PaymentIcon,
  School as SchoolIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { usePlanosPagamento } from '../../contexts/PlanosPagamentoContext';
import { formatarMoeda } from '../../utils/formatters';

const DetalhesPlanoPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { obterPlanoPagamento } = usePlanosPagamento();
  const [plano, setPlano] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const carregarPlano = async () => {
      try {
        setLoading(true);
        const data = await obterPlanoPagamento(id);
        if (data.success) {
          setPlano(data.data);
        } else {
          setError('Erro ao carregar dados do plano');
        }
      } catch (error) {
        console.error('Erro ao carregar plano:', error);
        setError('Erro ao carregar dados do plano');
      } finally {
        setLoading(false);
      }
    };

    carregarPlano();
  }, [id, obterPlanoPagamento]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/planos-pagamento')}
        >
          Voltar para Planos
        </Button>
      </Box>
    );
  }

  if (!plano) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Plano não encontrado
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/planos-pagamento')}
        >
          Voltar para Planos
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Detalhes do Plano de Pagamento
          </Typography>
          <Chip
            label={plano.tipo === 'BASE' ? 'Plano Base' : 'Plano Individual'}
            color={plano.tipo === 'BASE' ? 'primary' : 'secondary'}
            sx={{ mr: 1 }}
          />
          <Chip
            label={plano.status}
            color={plano.status === 'ATIVO' ? 'success' : 'default'}
          />
        </Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/planos-pagamento')}
        >
          Voltar
        </Button>
      </Box>

      {plano.tipo === 'INDIVIDUAL' && plano.planoBase && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Este é um plano individual baseado no plano: {plano.planoBase.descricao}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Informações Básicas */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <InfoIcon sx={{ mr: 1 }} color="primary" />
                <Typography variant="h6">Informações Básicas</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography color="text.secondary">Ano Letivo</Typography>
                  <Typography variant="h6">{plano.anoLetivo}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography color="text.secondary">Valor Total</Typography>
                  <Typography variant="h6" color="primary">
                    {formatarMoeda(plano.valorTotal)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography color="text.secondary">Descrição</Typography>
                  <Typography>{plano.descricao}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography color="text.secondary">Data de Início</Typography>
                  <Typography>
                    {new Date(plano.dataInicio).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography color="text.secondary">Data de Fim</Typography>
                  <Typography>
                    {new Date(plano.dataFim).toLocaleDateString()}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Informações do Estudante (apenas para planos individuais) */}
        {plano.tipo === 'INDIVIDUAL' && plano.estudante && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SchoolIcon sx={{ mr: 1 }} color="primary" />
                  <Typography variant="h6">Dados do Estudante</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography color="text.secondary">Nome</Typography>
                    <Typography variant="h6">{plano.estudante.nome}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Prestações */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PaymentIcon sx={{ mr: 1 }} color="primary" />
                <Typography variant="h6">Prestações</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {plano.prestacoes?.length > 0 ? (
                <Grid container spacing={2}>
                  {plano.prestacoes.map((prestacao, index) => (
                    <Grid item xs={12} md={4} key={prestacao.id || index}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {prestacao.mes}
                          </Typography>
                          <Typography variant="h5" color="primary" gutterBottom>
                            {formatarMoeda(prestacao.valor)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <AccessTimeIcon sx={{ mr: 1 }} fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              Vencimento: {new Date(prestacao.dataVencimento).toLocaleDateString()}
                            </Typography>
                          </Box>
                          <Box sx={{ mt: 1 }}>
                            <Chip
                              label={prestacao.status}
                              color={
                                prestacao.status === 'PAGO'
                                  ? 'success'
                                  : prestacao.status === 'PENDENTE'
                                  ? 'warning'
                                  : 'error'
                              }
                              size="small"
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Alert severity="info">
                  Nenhuma prestação configurada para este plano.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Métodos de Pagamento */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PaymentIcon sx={{ mr: 1 }} color="primary" />
                <Typography variant="h6">Métodos de Pagamento</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {Object.entries(plano.metodosDisponiveisJSON || {})
                .filter(([_, disponivel]) => disponivel)
                .map(([metodo]) => (
                  <Chip
                    key={metodo}
                    label={metodo}
                    sx={{ mr: 1, mb: 1 }}
                    variant="outlined"
                  />
                ))
              }

              {Object.values(plano.metodosDisponiveisJSON || {}).filter(v => v).length === 0 && (
                <Alert severity="info">
                  Nenhum método de pagamento configurado.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DetalhesPlanoPage;